// src/pages/dashboard/customer/Nannies.jsx
import { useState, useEffect } from 'react';
import api from '../../../lib/api';
import NannyCard from '../../../components/nanny/NannyCard';
import NannyFilters from '../../../components/nanny/NannyFilters';
import NannyDetailModal from '../../../components/nanny/NannyDetailModal';
import { error } from '../../../components/common/Toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { Filter, X } from 'lucide-react';
import { logProfileStat } from '../../../lib/helpers';

export default function Nannies() {
  const [nannies, setNannies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const fetchNannies = async (filters = {}) => {
    setLoading(true);
    const params = new URLSearchParams({
      page,
      vetted_only: filters.vetted_only !== false,
      ...filters,
    }).toString();

    try {
      const res = await api.get(`/nannies?${params}`);
      setNannies(res.data.data || res.data);
      setTotal(res.data.total || res.data.length || res.data.data?.length || 0);
    } catch (err) {
      error('Failed to load nannies');
      console.error(err);
      setNannies([]);
      setTotal(0);
    } finally {
      setLoading(false); // ← THIS WAS MISSING IN CATCH BEFORE!
    }
  };

  useEffect(() => {
    fetchNannies();
  }, [page]);

  const handleFilter = (filters) => {
    setPage(1);
    fetchNannies(filters);
  };

  const handleCardClick = (nanny) => (e) => {
    e.stopPropagation();
    setSelected(nanny);
  };

  return (
    <div className="space-y-8 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold">Find Your Perfect Nanny</h1>
        <p className="text-muted mt-2 text-lg">Trusted, vetted nannies across Nigeria</p>
      </div>

      {/* Toggleable Filters */}
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-lg font-medium text-gray-700">
            {total} nanny{total !== 1 ? 's' : ''} found
          </p>

          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition shadow-sm"
          >
            {filtersOpen ? <X size={20} /> : <Filter size={20} />}
            <span>{filtersOpen ? 'Hide' : 'Show'} Filters</span>
          </button>
        </div>

        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            filtersOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <NannyFilters onFilter={handleFilter} />
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-20">
          <LoadingSpinner size="lg" text="Loading nannies..." />
        </div>
      ) : nannies.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-2xl text-gray-500">No nannies found</p>
          <p className="mt-4 text-gray-600">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {nannies.map((nanny) => (
              <div
                key={nanny.id}
                onClick={() => setSelected(nanny)} // ← This triggers modal
                className="cursor-pointer"
              >
                <NannyCard
              nanny={nanny}
              onViewDetails={(n) => {
                logProfileStat(n.profile?.id || n.id);  // ← Logs the view
                setSelected(n);                          // ← Opens modal
              }}
            />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {total > 12 && (
            <div className="flex justify-center items-center gap-4 mt-12">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-8 py-3 border border-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                Previous
              </button>

              <span className="text-lg font-medium px-6">Page {page}</span>

              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={nannies.length < 12}
                className="px-8 py-3 bg-primary text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <NannyDetailModal
        nanny={selected}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}